"use client"

import { Header } from "@/components/header"
import { Footer } from "@/components/footer"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import { Card } from "@/components/ui/card"
import { Progress } from "@/components/ui/progress"
import { MaintenancePage } from "@/components/maintenance-page"
import { Search, Filter, TrendingUp, Clock, CheckCircle2, Flame, Loader2, Rocket, Shield, Lock, Star, Activity } from "lucide-react"
import Link from "next/link"
import { useState, useEffect } from "react"

interface Presale {
  id: string
  slug: string
  name: string
  tokenSymbol: string
  tokenAddress: string
  logoUrl?: string
  status: string
  totalRaised: string
  softCap: string
  hardCap: string
  presaleRate: string
  liquidityPercent: number
  startTime: string
  endTime: string
  shortDescription?: string
  description: string
  isFeatured: boolean
  isKYC: boolean
  isAudit: boolean
  lockLiquidity: boolean
  contributorCount: number
  chainId: number
  deployments?: any[]
}

export function PresalesContent() {
  const [presales, setPresales] = useState<Presale[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState("")
  const [statusFilter, setStatusFilter] = useState<string>("all")
  const [now, setNow] = useState(new Date())
  const [maintenance, setMaintenance] = useState(false)

  // Update current time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setNow(new Date())
    }, 1000)
    return () => clearInterval(timer)
  }, [])

  useEffect(() => {
    const fetchPresales = async () => {
      try {
        const res = await fetch('/api/presales')
        const data = await res.json()

        // Check for maintenance mode
        if (data.maintenance) {
          setMaintenance(true)
          return
        }

        setPresales(data.presales || [])
      } catch (error) {
        console.error('Failed to fetch presales:', error)
        setMaintenance(true)
      } finally {
        setLoading(false)
      }
    }
    fetchPresales()
  }, [])

  // Show maintenance page if database is offline
  if (maintenance) {
    return <MaintenancePage message="Our services are temporarily unavailable. Please try again later." />
  }

  // Filter presales (hide internal TKS presale from public listing)
  const filteredPresales = presales.filter(presale => {
    if (presale.slug === 'tks-global') return false
    const matchesSearch = presale.name.toLowerCase().includes(search.toLowerCase()) ||
      presale.tokenSymbol.toLowerCase().includes(search.toLowerCase())
    const matchesStatus = statusFilter === "all" || presale.status.toLowerCase() === statusFilter
    return matchesSearch && matchesStatus
  })

  // Calculate presale status based on time
  const getPresaleStatus = (presale: Presale) => {
    const start = new Date(presale.startTime)
    const end = new Date(presale.endTime)

    if (now < start) return 'upcoming'
    if (now > end) return 'ended'
    return 'live'
  }

  // Calculate time remaining
  const getTimeRemaining = (presale: Presale) => {
    const start = new Date(presale.startTime)
    const end = new Date(presale.endTime)

    let diff: number
    let prefix = ""

    if (now < start) {
      diff = start.getTime() - now.getTime()
      prefix = "Starts in "
    } else if (now < end) {
      diff = end.getTime() - now.getTime()
      prefix = "Ends in "
    } else {
      return 'Ended'
    }

    const days = Math.floor(diff / (1000 * 60 * 60 * 24))
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
    const seconds = Math.floor((diff % (1000 * 60)) / 1000)

    if (days >= 1) {
      return `${prefix}${days}d ${hours}h`
    } else if (hours >= 1) {
      return `${prefix}${hours}h ${minutes}m`
    } else {
      return `${prefix}${minutes}m ${seconds}s`
    }
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      <main className="flex-1">
        {/* Hero Section */}
        <section className="pt-28 pb-16 md:pt-32 md:pb-24 bg-gradient-to-b from-primary/5 to-transparent">
          <div className="container">
            <div className="max-w-3xl mx-auto text-center space-y-6">
              <h1 className="text-4xl md:text-6xl font-bold tracking-tight text-balance">Explore Token Presales</h1>
              <p className="text-lg md:text-xl text-muted-foreground text-balance">
                Discover and invest in the next generation of crypto projects. All presales are verified and secure.
              </p>
            </div>
          </div>
        </section>

        {/* Filter and Search */}
        <section className="py-8 border-b border-border/40">
          <div className="container">
            <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
              <div className="flex items-center gap-4 w-full md:w-auto">
                <Button
                  variant={statusFilter === "all" ? "default" : "outline"}
                  className="gap-2"
                  onClick={() => setStatusFilter("all")}
                >
                  <Filter className="h-4 w-4" />
                  All
                </Button>
                <Button
                  variant={statusFilter === "live" ? "default" : "outline"}
                  className="gap-2"
                  onClick={() => setStatusFilter("live")}
                >
                  <Flame className="h-4 w-4" />
                  Live
                </Button>
                <Button
                  variant={statusFilter === "upcoming" ? "default" : "outline"}
                  className="gap-2"
                  onClick={() => setStatusFilter("upcoming")}
                >
                  <Clock className="h-4 w-4" />
                  Upcoming
                </Button>
              </div>

              <div className="relative w-full md:w-96">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search presales..."
                  className="pl-9"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>
          </div>
        </section>

        {/* Presales Grid */}
        <section className="py-12">
          <div className="container">
            {loading ? (
              <div className="text-center py-16">
                <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
                <p className="text-muted-foreground">Loading presales...</p>
              </div>
            ) : filteredPresales.length === 0 ? (
              <div className="text-center py-16">
                <Rocket className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">No Presales Found</h3>
                <p className="text-muted-foreground mb-4">
                  {search ? `No presales match "${search}"` : "Be the first to create a presale!"}
                </p>
                <Button asChild>
                  <Link href="/create-presale">Create Presale</Link>
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredPresales.map((presale) => {
                  const status = getPresaleStatus(presale)
                  const raised = parseFloat(presale.totalRaised) || 0
                  const hardCap = parseFloat(presale.hardCap) || 1
                  const progress = Math.min((raised / hardCap) * 100, 100)

                  return (
                    <Card key={presale.id} className="overflow-hidden hover:border-primary/50 transition-colors">
                      <div className="p-6 space-y-4">
                        {/* Header */}
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-3">
                            {presale.logoUrl ? (
                              <img
                                src={presale.logoUrl}
                                alt={presale.name}
                                className="h-12 w-12 rounded-full object-cover"
                              />
                            ) : (
                              <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                                <Rocket className="h-6 w-6 text-primary" />
                              </div>
                            )}
                            <div>
                              <div className="flex items-center gap-2">
                                <h3 className="font-semibold">{presale.name}</h3>
                                {presale.isFeatured && (
                                  <Badge variant="secondary" className="bg-yellow-500/10 text-yellow-500 border-yellow-500/20 px-1 py-0 text-[10px] h-4">
                                    <Star className="h-2 w-2 fill-current mr-0.5" />
                                    Featured
                                  </Badge>
                                )}
                              </div>
                              <p className="text-sm text-muted-foreground">{presale.tokenSymbol}</p>
                            </div>
                          </div>
                          <div className="flex flex-col items-end gap-2">
                            <Badge
                              variant={
                                status === "live"
                                  ? "default"
                                  : status === "upcoming"
                                    ? "secondary"
                                    : "outline"
                              }
                              className="gap-1"
                            >
                              {status === "live" ? (
                                <Flame className="h-3 w-3" />
                              ) : status === "upcoming" ? (
                                <Clock className="h-3 w-3" />
                              ) : (
                                <CheckCircle2 className="h-3 w-3" />
                              )}
                              {status.charAt(0).toUpperCase() + status.slice(1)}
                            </Badge>

                            {/* Trust Badges */}
                            <div className="flex items-center gap-1">
                              {presale.isAudit && (
                                <div title="Audited" className="p-1 rounded bg-[#7ed957]/10 text-[#7ed957]">
                                  <Shield className="h-3 w-3" />
                                </div>
                              )}
                              {presale.isKYC && (
                                <div title="KYC Verified" className="p-1 rounded bg-blue-500/10 text-blue-500">
                                  <Activity className="h-3 w-3" />
                                </div>
                              )}
                              {presale.lockLiquidity && (
                                <div title="Liquidity Locked" className="p-1 rounded bg-primary/10 text-primary">
                                  <Lock className="h-3 w-3" />
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Short Description */}
                        {presale.shortDescription && (
                          <p className="text-sm text-muted-foreground line-clamp-2 min-h-[40px]">
                            {presale.shortDescription}
                          </p>
                        )}

                        {/* Progress */}
                        <div className="space-y-2">
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-muted-foreground">Progress</span>
                            <span className="font-medium">{progress.toFixed(1)}%</span>
                          </div>
                          <Progress value={progress} className="h-2" />
                          <div className="flex items-center justify-between text-sm">
                            <span className="font-medium">{raised.toFixed(4)} BNB</span>
                            <span className="text-muted-foreground">{hardCap.toFixed(2)} BNB</span>
                          </div>
                        </div>

                        {/* Details */}
                        <div className="grid grid-cols-2 gap-y-3 gap-x-4 text-sm pt-4 border-t border-border/50">
                          <div>
                            <p className="text-xs text-muted-foreground mb-1">Soft Cap</p>
                            <p className="font-semibold">{presale.softCap} BNB</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground mb-1">Hard Cap</p>
                            <p className="font-semibold">{presale.hardCap} BNB</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground mb-1">Liquidity</p>
                            <p className="font-semibold">{presale.liquidityPercent}%</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground mb-1">Contributors</p>
                            <p className="font-semibold">{presale.contributorCount}</p>
                          </div>
                        </div>

                        {/* Footer */}
                        <div className="flex items-center justify-between pt-4 border-t border-border/40">
                          <div className="flex items-center gap-2 text-sm">
                            <Clock className="h-4 w-4 text-muted-foreground" />
                            <span className="text-muted-foreground">{getTimeRemaining(presale)}</span>
                          </div>
                          <Link href={`/presale/${presale.slug}`}>
                            <Button size="sm">View Details</Button>
                          </Link>
                        </div>
                      </div>
                    </Card>
                  )
                })}
              </div>
            )}
          </div>
        </section>

        {/* SEO explainer */}
        <section className="container max-w-2xl mx-auto py-12">
          <div className="rounded-xl bg-muted/30 p-6 text-sm text-muted-foreground space-y-3">
            <h2 className="text-base font-semibold text-foreground">Browse Verified Crypto Presales</h2>
            <p>
              TokenKickstarter lists live, upcoming, and completed token presales across BNB Chain, Ethereum,
              Base, Polygon, and more. Every project on this page is deployed through our audited smart contracts.
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Live presales</strong> — buy tokens now before they list on DEXs.</li>
              <li><strong>Upcoming launches</strong> — follow projects and get notified when they go live.</li>
              <li><strong>Completed rounds</strong> — review past performance and results.</li>
            </ul>
            <p>
              Use the filters to search by chain, status, or sort by amount raised. Each presale page shows full
              details including tokenomics, team KYC status, audit reports, and links to locked liquidity.
            </p>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  )
}
